import type { AppState } from '@/core/state';
import type { SubjectId } from '@/core/types';
import { catalog } from '@/data/catalog';
import { COMPETENCE_GOALS, CURRICULA, stageLabel } from '@/data/curriculum/structure';
import { fold, tokenize } from '@/core/util/misc';
import { L, getLang } from '@/i18n';

export type SearchKind = 'subject' | 'domain' | 'topic' | 'skill' | 'curriculum' | 'competence-goal' | 'course' | 'lesson' | 'note' | 'mistake' | 'question';

export interface SearchHit {
  kind: SearchKind;
  id: string;
  title: string;
  subtitle: string;
  score: number;
  route: string;
}

interface Doc {
  kind: SearchKind;
  id: string;
  title: string;
  subtitle: string;
  text: string;
  route: string;
  subjectId?: string;
  boost: number;
}

let staticIndex: Doc[] | null = null;
let staticLang = '';

function buildStaticIndex(): Doc[] {
  const docs: Doc[] = [];
  for (const s of catalog.subjects) docs.push({ kind: 'subject', id: s.id, title: s.name, subtitle: L(`Subject · ${s.nameNo}`, 'Fag'), text: `${s.name} ${s.nameNo} ${s.description}`, route: `/subjects/${s.id}`, subjectId: s.id, boost: 1.2 });
  for (const s of catalog.subjects) {
    for (const d of catalog.domainsOf(s.id)) {
      docs.push({ kind: 'domain', id: d.id, title: d.name, subtitle: `${L('Domain', 'Område')} · ${s.name}`, text: `${d.name} ${d.description}`, route: `/subjects/${s.id}?domain=${d.id}`, subjectId: s.id, boost: 1.1 });
      for (const t of catalog.topicsOf(d.id)) docs.push({ kind: 'topic', id: t.id, title: t.name, subtitle: `${L('Topic', 'Tema')} · ${d.name}`, text: t.name, route: `/subjects/${s.id}?domain=${d.id}`, subjectId: s.id, boost: 0.9 });
    }
  }
  for (const sk of catalog.skills) {
    docs.push({
      kind: 'skill', id: sk.id, title: sk.name, subtitle: `${L('Skill', 'Ferdighet')} · ${catalog.subject(sk.subjectId)?.name} · ${stageLabel(sk.stage)}${sk.track ? ` (${sk.track})` : ''}`,
      text: `${sk.name} ${sk.description} ${sk.aliases.join(' ')} ${sk.content.definitions.map((d) => d.term).join(' ')}`,
      route: `/skill/${sk.id}`, subjectId: sk.subjectId, boost: 1.3,
    });
  }
  for (const c of CURRICULA) docs.push({ kind: 'curriculum', id: c.id, title: c.title, subtitle: `${L('Official curriculum', 'Offisiell læreplan')} · ${c.code}`, text: `${c.title} ${c.code}`, route: `/subjects/${c.subjectId}?tab=curriculum`, subjectId: c.subjectId, boost: 0.8 });
  for (const g of COMPETENCE_GOALS) {
    const c = CURRICULA.find((x) => x.id === g.curriculumId)!;
    docs.push({ kind: 'competence-goal', id: g.id, title: `${L('Competence goals', 'Kompetansemål')} — ${c.title}, ${stageLabel(g.stage)}`, subtitle: L('Udir competence goals', 'Kompetansemål fra Udir'), text: `${c.title} competence goals kompetansemål ${g.stage}`, route: `/subjects/${c.subjectId}?tab=curriculum`, subjectId: c.subjectId, boost: 0.5 });
  }
  return docs;
}

function dynamicDocs(state: AppState): Doc[] {
  const docs: Doc[] = [];
  for (const c of state.courses) {
    docs.push({ kind: 'course', id: c.id, title: c.title, subtitle: L('Course', 'Kurs'), text: `${c.title} ${c.request}`, route: `/courses/${c.id}`, subjectId: c.subjectId, boost: 1 });
    for (const m of c.modules) for (const l of m.lessons) docs.push({ kind: 'lesson', id: l.id, title: l.title, subtitle: `${L('Lesson', 'Leksjon')} · ${c.title}`, text: l.title, route: `/courses/${c.id}`, subjectId: c.subjectId, boost: 0.7 });
  }
  for (const n of state.notes) docs.push({ kind: 'note', id: n.id, title: n.title || L('Untitled note', 'Notat uten tittel'), subtitle: `${L('Note', 'Notat')} · ${n.tags.map((t) => `#${t}`).join(' ')}`, text: `${n.title} ${n.body} ${n.tags.join(' ')}`, route: `/notes?id=${n.id}`, boost: 1 });
  for (const m of state.mistakes) docs.push({ kind: 'mistake', id: m.id, title: m.question.prompt.replace(/\*\*/g, '').slice(0, 80), subtitle: `${L('Mistake', 'Feil')} · ${catalog.skill(m.skillId)?.name}`, text: `${m.question.prompt} ${m.expected}`, route: '/mistakes', subjectId: m.subjectId, boost: 0.8 });
  const seen = new Set<string>();
  for (const a of state.attempts.slice(-300).reverse()) {
    if (seen.has(a.fingerprint)) continue;
    seen.add(a.fingerprint);
    docs.push({ kind: 'question', id: a.id, title: a.prompt.replace(/\*\*/g, '').slice(0, 80), subtitle: `${L('Question you answered', 'Oppgave du har svart på')} · ${catalog.skill(a.skillId)?.name}`, text: a.prompt, route: `/skill/${a.skillId}`, subjectId: a.subjectId, boost: 0.5 });
  }
  return docs;
}

export function searchCatalog(query: string, opts: { kinds?: SearchKind[]; subjectId?: SubjectId; limit?: number; state?: AppState } = {}): SearchHit[] {
  if (!staticIndex || staticLang !== getLang()) {
    staticIndex = buildStaticIndex();
    staticLang = getLang();
  }
  const terms = tokenize(query);
  if (!terms.length) return [];
  const q = fold(query.trim());
  const docs = opts.state ? [...staticIndex, ...dynamicDocs(opts.state)] : staticIndex;
  const hits: SearchHit[] = [];
  for (const d of docs) {
    if (opts.kinds && !opts.kinds.includes(d.kind)) continue;
    if (opts.subjectId && d.subjectId && d.subjectId !== opts.subjectId) continue;
    const title = fold(d.title);
    const text = fold(d.text);
    let score = 0;
    if (title === q) score += 12;
    else if (title.startsWith(q)) score += 7;
    else if (title.includes(q)) score += 5;
    let matched = 0;
    for (const t of terms) {
      if (title.includes(t)) { score += 3; matched++; }
      else if (text.includes(t)) { score += 1.2; matched++; }
      else if (t.length > 4 && text.includes(t.slice(0, -1))) { score += 0.6; matched++; }
    }
    if (!matched && score === 0) continue;
    score *= (matched / terms.length) * d.boost;
    if (score > 0.5) hits.push({ kind: d.kind, id: d.id, title: d.title, subtitle: d.subtitle, score, route: d.route });
  }
  return hits.sort((a, b) => b.score - a.score).slice(0, opts.limit ?? 30);
}
